import { DOWNSAMPLE_MODES, LIMITS, outputSize, type Params } from '../shared/params';
import { Group, NumberField, Select, Slider, Toggle } from './controls';
import { PRESETS } from './presets';
import { useT } from './i18n';
import type { StringKey } from './i18n';

interface Props {
  params: Params;
  disabled: boolean;
  onChange: (patch: Partial<Params>) => void;
  onPreset: (params: Params) => void;
}

/** Which preset, if any, the current parameters correspond to exactly. */
function activePresetId(params: Params): string | null {
  for (const preset of PRESETS) {
    const keys = Object.keys(preset.params) as (keyof Params)[];
    if (keys.every((k) => preset.params[k] === params[k])) return preset.id;
  }
  return null;
}

export function Panel({ params, disabled, onChange, onPreset }: Props) {
  const t = useT();
  const out = outputSize(params);
  const active = activePresetId(params);
  const set = <K extends keyof Params>(key: K) => (v: Params[K]) => onChange({ [key]: v } as Partial<Params>);

  return (
    <div class="panel" style={{ opacity: disabled ? 0.55 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div class="presets">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            class={`chip${active === p.id ? ' active' : ''}`}
            aria-pressed={active === p.id}
            onClick={() => onPreset({ ...p.params })}
          >
            {t(p.labelKey)}
          </button>
        ))}
      </div>

      <Group title={t('group.size')}>
        <div class="row" style={{ gap: 10, alignItems: 'flex-end' }}>
          <NumberField
            label={t('field.targetW')}
            value={params.targetW}
            min={LIMITS.target.min}
            max={LIMITS.target.max}
            onChange={set('targetW')}
          />
          <NumberField
            label={t('field.targetH')}
            value={params.targetH}
            min={LIMITS.target.min}
            max={LIMITS.target.max}
            onChange={set('targetH')}
          />
        </div>
        <Toggle label={t('field.bind')} checked={params.bind} onChange={set('bind')} />
        <Slider
          label={t('field.pixelSize')}
          value={params.pixelSize}
          min={LIMITS.pixelSize.min}
          max={LIMITS.pixelSize.max}
          onChange={set('pixelSize')}
        />
        <div class="output-size">
          <span>{t('field.output')}</span>
          <b>
            {out.w} × {out.h}
          </b>
        </div>
      </Group>

      <Group title={t('group.outline')}>
        <Slider
          label={t('field.thickness')}
          value={params.thickness}
          min={LIMITS.thickness.min}
          max={LIMITS.thickness.max}
          hint={params.thickness === 0 ? t('thickness.off') : undefined}
          onChange={set('thickness')}
        />
      </Group>

      <Group title={t('group.downscale')}>
        <Select
          label={t('field.mode')}
          value={params.mode}
          options={DOWNSAMPLE_MODES.map((m) => ({ value: m, label: m }))}
          onChange={set('mode')}
        />
      </Group>

      <Group title={t('group.sharpen')} open={false}>
        <Select
          label={t('field.sharpenMode')}
          value={params.sharpenMode}
          options={[
            { value: 'none', label: t('thickness.off') },
            { value: 'unsharp', label: 'unsharp' },
            { value: 'laplacian', label: 'laplacian' },
          ]}
          onChange={set('sharpenMode')}
        />
      </Group>

      <Group title={t('group.color')}>
        <Toggle
          label={t('field.doColorMatch')}
          checked={params.doColorMatch}
          onChange={set('doColorMatch')}
        />
        <Toggle label={t('field.doQuant')} checked={params.doQuant} onChange={set('doQuant')} />
        {params.doQuant && (
          <>
            <Slider
              label={t('field.numColors')}
              value={params.numColors}
              min={LIMITS.numColors.min}
              max={LIMITS.numColors.max}
              onChange={set('numColors')}
            />
            <Select
              label={t('field.quantMode')}
              value={params.quantMode}
              options={[
                { value: 'kmeans', label: 'kmeans' },
                { value: 'weighted-kmeans', label: 'weighted-kmeans' },
                { value: 'repeat-kmeans', label: 'repeat-kmeans' },
              ]}
              onChange={set('quantMode')}
            />
            <Select
              label={t('field.ditherMode')}
              value={params.ditherMode}
              options={[
                { value: 'ordered', label: 'ordered' },
                { value: 'error_diffusion', label: 'error_diffusion' },
                { value: 'none', label: t('thickness.off') },
              ]}
              onChange={set('ditherMode')}
            />
          </>
        )}
      </Group>

      <Group title={t('group.output')} open={false}>
        <Toggle
          label={t('field.noPostUpscale')}
          checked={params.noPostUpscale}
          onChange={set('noPostUpscale')}
        />
      </Group>
    </div>
  );
}

export type { StringKey };
